/**
 * The dispatch board.
 *
 * Three lists that only mean anything together: an order waiting, a rider free
 * to take it, and what is already out. So they are fetched in one call and
 * shown side by side, which is also how a dispatcher holds them in their head.
 *
 * Polls, because a dispatcher does not refresh a screen — new delivery orders
 * arrive from the till while they are looking at it.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatPKR } from '@foodrun/shared';

import { api } from '../../lib/api.js';
import { cashOnDelivery, headroom, rankForOrder, waitingMinutes } from './dispatch.js';
import { AssignSheet } from './AssignSheet.jsx';
import { RiderCashSheet } from './RiderCashSheet.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { lastSeen } from './lastSeen.js';
import { Spinner } from '../../components/ui/Spinner.jsx';

/** Often enough to feel live on a local network, rarely enough to cost nothing. */
const POLL_MS = 10_000;

export default function DispatchPage() {
  const client = useQueryClient();
  const [assigning, setAssigning] = useState(null);
  const [cashFor, setCashFor] = useState(null);

  const board = useQuery({
    queryKey: ['delivery', 'board'],
    queryFn: () => api.get('/delivery/board'),
    refetchInterval: POLL_MS,
    placeholderData: (previous) => previous,
  });

  const refresh = () => client.invalidateQueries({ queryKey: ['delivery'] });

  const assign = useMutation({
    mutationFn: ({ orderId, riderId }) =>
      api.post(`/delivery/orders/${orderId}/assign`, { riderId }),
    onSuccess: () => {
      setAssigning(null);
      refresh();
    },
  });

  const advance = useMutation({
    mutationFn: ({ id, action, body }) => api.post(`/delivery/assignments/${id}/${action}`, body),
    onSuccess: refresh,
  });

  if (board.isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Spinner label="Loading the board" />
      </div>
    );
  }

  const waiting = board.data?.waiting ?? [];
  const out = board.data?.out ?? [];
  const riders = board.data?.riders ?? [];

  return (
    <div className="p-4">
      <h1 className="mb-4 font-semibold text-xl text-ink">Delivery</h1>

      <div className="grid gap-4 lg:grid-cols-3">
        <Column title="Waiting to go out" count={waiting.length}>
          {waiting.length === 0 ? (
            <Empty>Nothing waiting.</Empty>
          ) : (
            // Only the oldest handful: a dispatcher sends the next order, not
            // the four hundredth. The count above says how many there are.
            waiting
              .slice(0, 25)
              .map((order) => (
                <WaitingCard key={order.id} order={order} onAssign={() => setAssigning(order)} />
              ))
          )}
          {waiting.length > 25 && (
            <p className="px-1 py-2 text-xs text-ink-3">
              and {waiting.length - 25} more, oldest first
            </p>
          )}
        </Column>

        <Column title="Out with riders" count={out.length}>
          {out.length === 0 ? (
            <Empty>Nothing out.</Empty>
          ) : (
            out.map((assignment) => (
              <OutCard
                key={assignment.id}
                assignment={assignment}
                busy={advance.isPending}
                onAdvance={(action, body) => advance.mutate({ id: assignment.id, action, body })}
              />
            ))
          )}
        </Column>

        <Column title="Riders" count={riders.length}>
          {riders.length === 0 ? (
            <Empty>No riders set up yet.</Empty>
          ) : (
            riders.map((rider) => (
              <RiderCard key={rider.id} rider={rider} onCash={() => setCashFor(rider)} />
            ))
          )}
        </Column>
      </div>

      {assigning && (
        <AssignSheet
          order={assigning}
          riders={rankForOrder(riders, assigning)}
          busy={assign.isPending}
          error={assign.error}
          onCancel={() => {
            assign.reset();
            setAssigning(null);
          }}
          onAssign={(riderId) => assign.mutate({ orderId: assigning.id, riderId })}
        />
      )}

      {cashFor && <RiderCashSheet rider={cashFor} onClose={() => setCashFor(null)} />}
    </div>
  );
}

function Column({ title, count, children }) {
  return (
    <section className="rounded-xl border border-rule bg-surface p-3">
      <h2 className="mb-2 flex items-baseline justify-between text-sm font-medium text-ink-2">
        {title}
        <span className="text-xs tabular-nums text-ink-3">{count}</span>
      </h2>
      <div className="max-h-[70vh] space-y-2 overflow-y-auto">{children}</div>
    </section>
  );
}

function Empty({ children }) {
  return <p className="py-8 text-center text-sm text-ink-3">{children}</p>;
}

function WaitingCard({ order, onAssign }) {
  const owed = cashOnDelivery(order);
  const minutes = waitingMinutes(order);

  return (
    <div className="rounded-lg border border-rule bg-surface-3 p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium text-ink">{order.order_number}</span>
        <span
          className={`text-sm tabular-nums ${minutes >= 20 ? 'text-bad' : minutes >= 10 ? 'text-warn' : 'text-ink-3'}`}
        >
          {minutes}m
        </span>
      </div>

      <p className="mt-0.5 truncate text-sm text-ink-2">
        {order.customer_name || 'No name'}
        {order.customer_phone && ` · ${order.customer_phone}`}
      </p>
      {order.delivery_address && (
        <p className="mt-0.5 line-clamp-2 text-xs text-ink-3">{order.delivery_address}</p>
      )}

      <div className="mt-2 flex items-center justify-between gap-2">
        {owed > 0 ? (
          <span className="rounded-full bg-warn-bg px-2 py-0.5 text-xs font-medium text-warn">
            {formatPKR(owed)} to collect
          </span>
        ) : (
          <span className="text-xs text-ink-3">Already paid</span>
        )}

        <Button size="sm" onClick={onAssign}>
          Send
        </Button>
      </div>
    </div>
  );
}

function OutCard({ assignment, onAdvance, busy }) {
  const [failing, setFailing] = useState(false);
  const [reason, setReason] = useState('');

  const next = { assigned: 'picked_up', accepted: 'picked_up', picked_up: 'delivered' }[
    assignment.status
  ];

  return (
    <div className="rounded-lg border border-rule bg-surface-3 p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium text-ink">{assignment.order_number}</span>
        <span
          className={`text-sm tabular-nums ${
            assignment.minutes_out >= 45
              ? 'text-bad'
              : assignment.minutes_out >= 25
                ? 'text-warn'
                : 'text-ink-3'
          }`}
        >
          {assignment.minutes_out}m
        </span>
      </div>

      <p className="mt-0.5 text-sm text-ink-2">
        {assignment.rider_name}
        {assignment.rider_phone && ` · ${assignment.rider_phone}`}
      </p>
      {assignment.cod_amount > 0 && (
        <p className="mt-0.5 text-xs text-warn">{formatPKR(assignment.cod_amount)} to collect</p>
      )}

      {failing ? (
        <div className="mt-2">
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="What happened?"
            aria-label="Why the delivery failed"
            className="h-10 w-full rounded-lg border border-rule bg-surface px-3 text-sm text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
          />
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => setFailing(false)}>
              Back
            </Button>
            <Button
              size="sm"
              variant="danger"
              className="flex-1"
              // A failure with no reason is the one nobody can look into later.
              disabled={!reason.trim() || busy}
              onClick={() => onAdvance('failed', { reason: reason.trim() })}
            >
              Record failure
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => setFailing(true)}>
            Failed
          </Button>
          {next && (
            <Button
              size="sm"
              className="flex-1"
              busy={busy}
              onClick={() => onAdvance(next === 'picked_up' ? 'picked-up' : 'delivered')}
            >
              {next === 'picked_up' ? 'Picked up' : 'Delivered'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function RiderCard({ rider, onCash }) {
  const room = headroom(rider);
  const full = room <= 0;

  return (
    <button
      onClick={onCash}
      className="w-full rounded-lg border border-rule bg-surface-3 p-3 text-left hover:border-brand-400"
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium text-ink">{rider.name}</span>
        <span className="text-xs text-ink-3">{rider.status?.replace('_', ' ')}</span>
      </div>

      <div className="mt-1 flex items-baseline justify-between gap-2 text-sm">
        <span className="text-ink-2">
          Holding <span className="tabular-nums text-ink">{formatPKR(rider.cod_balance)}</span>
        </span>
        <span className={`text-xs tabular-nums ${full ? 'text-bad' : 'text-ink-3'}`}>
          {full ? 'at their limit' : `room for ${formatPKR(room)}`}
        </span>
      </div>

      {rider.orders_out > 0 && (
        <p className="mt-0.5 text-xs text-ink-3">
          {rider.orders_out} order{rider.orders_out === 1 ? '' : 's'} out
        </p>
      )}

      {/*
        When the phone last said where it was.

        The age is the useful part, not the coordinate. "Nine minutes ago"
        tells a dispatcher the rider is out there with the app open; a
        position from two hours ago tells them the dot on any map would be a
        lie, which is worth knowing before they send the next order by it.
      */}
      <p className="mt-0.5 text-xs text-ink-3">{lastSeen(rider)}</p>
    </button>
  );
}

